import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'
import Button from 'li-components/lib/next/Button'
import SvgIcon from 'li-components/lib/SvgIcon'
import Select from 'li-components/lib/Field/Select'
import Spinner from 'li-components/lib/Spinner'
import TargetBlank from 'li-components/lib/TargetBlank'

import { getAppDotOrigin, isEmpty, tourName, track } from '../../../util'
import {
  Sidebar,
  SidebarHeader,
  SidebarHeaderDrilled,
  SidebarMain,
  SidebarMainMiddleSection,
  SidebarMainBottomSection,
  SidebarMainBottomMenuItem,
  SidebarFooter,
  SidebarCloseButton,
  SidebarListItem,
  List,
} from '../../styled'
import PlatformNav from '../PlatformNav'
import {
  PlatformNavList,
  UserSection,
  UserIcon,
  Impersonating,
  BackButton,
} from './styled'

export default class UserMenu extends Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    impersonatedUser: PropTypes.object.isRequired,
    stopImpersonating: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    onLogout: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onChangeOrganization: PropTypes.func.isRequired,
    profileChangeInProgress: PropTypes.bool.isRequired,
    isInternal: PropTypes.bool.isRequired,
  }

  static contextTypes = {
    customNav: PropTypes.func,
    appId: PropTypes.string.isRequired,
    getEffectiveUser: PropTypes.func.isRequired,
  }

  origin = getAppDotOrigin()

  state = {
    isAdminApp: false,
    isUserAdmin: false,
    showOrganizationSwitcher: false,
  }

  componentWillMount() {
    const { user } = this.props
    const isUserAdmin = user.roles.includes('ROLE_USER_ADMIN')

    this.setState({
      isAdminApp: this.context.appId === 'admin',
      isUserAdmin,
    })
  }

  qualifyAdminPath(pathname) {
    return this.state.isAdminApp ? pathname : `${this.origin}${pathname}`
  }

  toggleShowOrganizationSwitcher = () => {
    if (!this.props.profileChangeInProgress) {
      const showOrganizationSwitcher = !this.state.showOrganizationSwitcher
      this.setState({ showOrganizationSwitcher })
      track.userMenu({
        event: showOrganizationSwitcher
          ? 'enterOrganizationSwitcher'
          : 'exitOrganizationSwitcher',
      })
    }
  }

  onChangeOrganization = e => {
    this.props.onChangeOrganization(e.currentTarget.value)
    track.userMenu({
      event: 'changeOrganization',
      label: e.currentTarget.value,
    })
  }

  canSwitchOrganization() {
    return (
      !this.props.isInternal &&
      isEmpty(this.props.impersonatedUser) &&
      this.props.user.profiles.length > 1
    )
  }

  onClose = () => {
    this.props.onClose()
    track.userMenu({ event: 'exit', label: 'closeButtonClick' })
  }

  onClickOutside = () => {
    if (this.props.open && !this.props.profileChangeInProgress) {
      this.props.onClose()
      track.userMenu({ event: 'exit', label: 'outsideClick' })
    }
  }

  onStopImpersonating = () => {
    this.props.stopImpersonating()
    track.userMenu({ event: 'stopImpersonatingClick' })
  }

  onLogout = e => {
    e.preventDefault()
    this.props.onLogout()
    track.userMenu({ event: 'logoutClick' })
  }

  render() {
    const { open, user, impersonatedUser, profileChangeInProgress } = this.props
    const { customNav, appId } = this.context
    const canSwitchOrganization = this.canSwitchOrganization()
    const effectiveUser = this.context.getEffectiveUser()
    const isImpersonating = !!impersonatedUser.email

    return (
      <Sidebar open={open} onClickOutside={this.onClickOutside}>
        {canSwitchOrganization && this.state.showOrganizationSwitcher ? (
          <SidebarHeaderDrilled
            impersonating={isImpersonating}
            onClick={this.toggleShowOrganizationSwitcher}
            data-tour={tourName('exit-org-switcher')}
          >
            <BackButton>
              <SvgIcon name="left-2" />
            </BackButton>
            <h2>Switch Organization</h2>
          </SidebarHeaderDrilled>
        ) : (
          <SidebarHeader impersonating={isImpersonating}>
            <UserSection>
              <UserIcon />
              <main>
                <div>{effectiveUser.email}</div>
                {effectiveUser.profiles.length > 1 && (
                  <div>{effectiveUser.organizationName}</div>
                )}
              </main>
            </UserSection>
            <SidebarCloseButton
              data-tour={tourName('close-user-menu')}
              onClick={this.onClose}
            >
              &times;
            </SidebarCloseButton>
          </SidebarHeader>
        )}
        {canSwitchOrganization && this.state.showOrganizationSwitcher ? (
          <SidebarMain>
            <section style={{ padding: '2rem' }}>
              {profileChangeInProgress ? (
                <Spinner isShowing />
              ) : (
                <Select
                  label="Organization"
                  value={user.organization}
                  onChange={this.onChangeOrganization}
                  cols={12}
                  fieldProps={{
                    'data-tour': tourName('org-switcher'),
                  }}
                >
                  {user.profiles.map(({ organizationId, organizationName }) => (
                    <option key={organizationId} value={organizationId}>
                      {organizationName}
                    </option>
                  ))}
                </Select>
              )}
            </section>
          </SidebarMain>
        ) : (
          <SidebarMain>
            <SidebarMainMiddleSection>
              <List>
                {isImpersonating && (
                  <Impersonating
                    className="Impersonating"
                    onClick={this.onStopImpersonating}
                  >
                    <SvgIcon name="x-2" />
                    <span>Stop Impersonating</span>
                  </Impersonating>
                )}
                {this.state.isUserAdmin && (
                  <SidebarListItem
                    data-tour={tourName('navlink-admin')}
                    onClick={() => {
                      track.userMenu({ event: 'adminLinkClick' })
                    }}
                  >
                    {this.state.isAdminApp ? (
                      <Link to={this.qualifyAdminPath('/admin')}>Admin</Link>
                    ) : (
                      <a href={this.qualifyAdminPath('/admin')}>Admin</a>
                    )}
                    <SvgIcon name="right-2" />
                  </SidebarListItem>
                )}
                {canSwitchOrganization && (
                  <SidebarListItem
                    onClick={this.toggleShowOrganizationSwitcher}
                    data-tour={tourName('enter-org-switcher')}
                  >
                    Switch Organization <SvgIcon name="right-2" />
                  </SidebarListItem>
                )}
              </List>
              <PlatformNavList>
                <PlatformNav customNav={customNav} appId={appId} isSidebarNav />
              </PlatformNavList>
            </SidebarMainMiddleSection>
            <SidebarMainBottomSection>
              <List>
                <SidebarMainBottomMenuItem>
                  <TargetBlank
                    href="https://www.labelinsight.com"
                    title="Learn More"
                    data-tour={tourName('navlink-learn-more')}
                    onClick={() => {
                      track.userMenu({ event: 'learnMoreLinkClick' })
                    }}
                  >
                    <h4>Learn More About Label Insight</h4>
                    <SvgIcon name="external-link-square-2" />
                  </TargetBlank>
                </SidebarMainBottomMenuItem>
              </List>
            </SidebarMainBottomSection>
          </SidebarMain>
        )}
        <SidebarFooter>
          <a
            href="/logout"
            className="logout"
            target="_self"
            onClick={this.onLogout}
            data-tour={tourName('logout')}
          >
            <Button kind="primary" block>
              LOG OUT
            </Button>
          </a>
        </SidebarFooter>
      </Sidebar>
    )
  }
}



// WEBPACK FOOTER //
// ./~/label-insight-app-shell/src/Application/components/Header/SidebarNav/UserMenu.js